import React, { useEffect, useMemo } from 'react';

import {
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';

import {
  adminActions,
  campaignDefaultTemplate,
  HelpTypes,
  Language,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { showSuccess } from '../../Profile/Pharmacist/PharmacistViewProfile/helper';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import CustomBreadcrumbs from '../../../common/CustomBreadcrumbs';
import CustomDropdown from '../../../common/CustomDropdown';
import { formKeys, InputTypes } from '../../../../helpers/Constants';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';

import CampaignEmailTemplate from './CampaignEmailTemplate';
import CampaignEmailImage from './CampaignEmailImage';
import { crumbs, emailTemplateItems } from './helper';
import styles from './styles';
import useConfigureCampaignEmail from '../../../../hooks/Admin/Campaigns/useConfigureCampaignEmail';
import CustomTextField from '../../../common/CustomTextField';

enum CampaignEmailLanguage {
  en = 'en',
  fr = 'fr',
}

interface IConfigureCampaignEmail {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  userType: HelpTypes;
}

const ConfigureCampaignEmail = ({
  setCurrentStep,
  userType,
}: IConfigureCampaignEmail) => {
  const dispatch = useAppDispatch();

  const defaultTemplates = useAppSelector(campaignDefaultTemplate);
  const {
    placeholders,
    bookingPlaceholders,
    conditions,
    leadActivationPlaceholders,
    mdsBookingPlaceholders,
    sendInvitePlaceholders,
  } = defaultTemplates ?? {};

  const sendSuccess = useAppSelector((store) =>
    successSelector([adminActions.sendTestCampaignMail], store));

  const {
    formik,
    testEmailFormik,
    isSendDisabled,
    isTestEmailDisabled,
    getActiveFieldNames,
    language,
    setLanguage,
    onSendTestEmail,
    onEditorReady,
  } = useConfigureCampaignEmail(setCurrentStep, userType);

  const placeholderOptions = useMemo(
    () => {
      const placeholderMap = {
        [formKeys.activationEmail]: leadActivationPlaceholders,
        [formKeys.multiBookingEmail]: mdsBookingPlaceholders,
        [formKeys.bookingEmail]: bookingPlaceholders,
        [formKeys.existingUserEmail]: sendInvitePlaceholders,
        [formKeys.registrationEmail]: sendInvitePlaceholders,
      };

      return (
        placeholderMap?.[
          formik.values.emailTemplateItem as keyof typeof placeholderMap
        ] ?? placeholders
      ).map((item) =>
        ({
          key: item,
          label: item,
        }));
    },
    [leadActivationPlaceholders, mdsBookingPlaceholders, bookingPlaceholders, sendInvitePlaceholders, formik.values.emailTemplateItem, placeholders],
  );

  const isCampaignTypeSelected = !!formik.values.emailTemplateItem;
  const isLeadInvite = formik.values.emailTemplateItem === formKeys.registrationEmail
    || formik.values.emailTemplateItem === formKeys.existingUserEmail;
  const activeConditionTag = conditions?.[0]?.key;

  const handleLanguageChange = (
    _: React.MouseEvent<HTMLElement>,
    nextLanguage: Language | null,
  ) => {
    if (nextLanguage) {
      setLanguage(nextLanguage);
    }
  };
  useEffect(() => {
    if (sendSuccess) {
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
      dispatch(resetStatus([adminActions.sendTestCampaignMail]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendSuccess]);

  useEffect(() => {
    if (isLeadInvite) {
      setLanguage(Language.fr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeadInvite]);

  const crumb = useMemo(() =>
    crumbs(() =>
      setCurrentStep((prev) =>

        prev - 1)), []);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.crumbContainer}>
        <CustomBreadcrumbs crumbs={crumb} />
      </Box>

      <Typography sx={styles.title}>{strings.createEmailTemplate}</Typography>

      <Box sx={styles.placeholderContainer}>
        <CustomDropdown
          label={strings.campaignType}
          menuItems={emailTemplateItems()}
          formik={formik}
          name="emailTemplateItem"
        />
      </Box>

      {isCampaignTypeSelected && (
        <>
          <ToggleButtonGroup
            exclusive
            value={language}
            color="primary"
            onChange={handleLanguageChange}
            sx={styles.languageTabs}
          >
            <ToggleButton value={CampaignEmailLanguage.fr}>
              {strings.french}
            </ToggleButton>
            {!isLeadInvite && (
              <ToggleButton value={CampaignEmailLanguage.en}>
                {strings.english}
              </ToggleButton>
            )}
          </ToggleButtonGroup>

          <Divider sx={{ mb: 1 }} />

          <CampaignEmailImage
            formik={formik}
            name={getActiveFieldNames.imageName}
          />

          <CampaignEmailTemplate
            formik={formik}
            bodyName={getActiveFieldNames.bodyName}
            onEditorReady={onEditorReady}
            subjectName={getActiveFieldNames.subjectName}
            conditionTag={activeConditionTag}
            conditionTagOptions={conditions}
            placeholderOptions={placeholderOptions}
          />

          <Box sx={styles.testEmailContainer} mt={2}>
            <Typography sx={styles.rowLabel}>
              {strings.sendTestEmail}
            </Typography>
            <Box flex={6} display="flex">
              <CustomTextField
                customTextFieldStyle={styles.testEmail}
                name="email"
                placeholder={strings.enterEmail}
                type={InputTypes.email}
                formik={testEmailFormik}
              />
              <CustomButton
                disabled={isTestEmailDisabled}
                label={strings.send}
                onClick={onSendTestEmail}
              />
            </Box>
          </Box>

          <Box sx={styles.buttonContainer}>
            <CustomButton
              disabled={isSendDisabled}
              label={strings.save}
              onClick={formik.handleSubmit}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ConfigureCampaignEmail;
